'use client';

/**
 * TestResultsWidget - Graph-native test results display
 *
 * Queries Neo4j for test run results based on graph selection.
 * No Zustand store - the graph is the source of truth.
 *
 * Displays:
 * - Test case metadata (description, user_outcome, business_value)
 * - Execution trace (steps with request/response)
 * - Products found (for discovery tests)
 * - Conversation flow for multi-turn tests
 *
 * @component_role Test Results Visualization
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  CheckCircle2,
  XCircle,
  Package,
  Clock,
  ChevronRight,
  ChevronDown,
  Loader2,
  RefreshCw,
  Target,
  Briefcase,
  FileText,
  ArrowRight,
  Link2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Types (aligned with router.py TestRunResponse)
// ---------------------------------------------------------------------------

interface TestStep {
  step_name: string;
  method: string;
  endpoint: string;
  request_body?: Record<string, unknown> | null;
  status_code?: number | null;
  response_summary?: string | null;
  duration_ms?: number | null;
  success: boolean;
}

interface ProductItem {
  semantic_id: string;
  name: string;
  entity_type: string;
  similarity?: number;
  image?: string;
  price?: number;
  vendor?: string;
}

interface ExecutedResult {
  case_id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  user_outcome?: string | null;
  business_value?: string | null;
  apis_tested?: string[] | null;
  success: boolean;
  duration_ms?: number | null;
  summary?: string | null;
  steps: TestStep[];
  details: {
    products?: ProductItem[];
    product_count?: number;
    conversation_id?: string;
    [key: string]: unknown;
  };
  executed_at?: string | null;
}

interface TestRunResponse {
  run_id: string;
  name?: string | null;
  run_type?: string | null;
  category?: string | null;
  timestamp?: string | null;
  environment?: string | null;
  trigger?: string | null;
  triggered_by?: string | null;
  total: number;
  passed: number;
  failed: number;
  pass_rate?: number | null;
  duration_ms?: number | null;
  results: ExecutedResult[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TestResultsWidget() {
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const [data, setData] = useState<TestRunResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch test run when selection changes
  const fetchTestRun = async (runId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<TestRunResponse>(`/testing/runs/${runId}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test run');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedNode?.type === 'test_run' && selectedNode.id) {
      fetchTestRun(selectedNode.id);
    } else {
      setData(null);
    }
  }, [selectedNode?.id, selectedNode?.type]);

  // Handle product selection
  const handleSelectProduct = (product: ProductItem) => {
    selectNode({
      id: product.semantic_id,
      type: product.entity_type || 'product',
      label: product.name,
    });
  };

  // No selection state
  if (!selectedNode || selectedNode.type !== 'test_run') {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No test run selected</p>
          <p className="text-xs mt-1">Run a test to see results here</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchTestRun(selectedNode.id)}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // No data
  if (!data) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <p className="text-sm">Test run data not available</p>
      </div>
    );
  }

  // Single result or suite?
  const isSingleTest = data.results.length === 1;
  const result = isSingleTest ? data.results[0] : null;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <RunHeader run={data} onRefresh={() => fetchTestRun(selectedNode.id)} />

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isSingleTest && result ? (
          <SingleTestResult result={result} onSelectProduct={handleSelectProduct} />
        ) : (
          <SuiteResults results={data.results} onSelectProduct={handleSelectProduct} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run Header
// ---------------------------------------------------------------------------

function RunHeader({ run, onRefresh }: { run: TestRunResponse; onRefresh: () => void }) {
  const allPassed = run.failed === 0;

  return (
    <div className="border-b border-border/50 bg-muted/30">
      {/* Title row */}
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          {allPassed ? (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <span className="font-medium">{run.name || run.run_id}</span>
          <Badge
            variant={allPassed ? 'default' : 'destructive'}
            className="text-xs"
          >
            {run.passed}/{run.total} passed
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {run.duration_ms ? `${Math.round(run.duration_ms)}ms` : '-'}
          </span>
          <Button size="sm" variant="ghost" onClick={onRefresh} className="h-7 w-7 p-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Meta row */}
      <div className="px-4 pb-2 flex items-center gap-3 text-xs text-muted-foreground">
        {run.trigger && <span>Trigger: {run.trigger}</span>}
        {run.environment && <span>Env: {run.environment}</span>}
        {run.timestamp && (
          <span>{new Date(run.timestamp).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single Test Result
// ---------------------------------------------------------------------------

function SingleTestResult({
  result,
  onSelectProduct,
}: {
  result: ExecutedResult;
  onSelectProduct: (p: ProductItem) => void;
}) {
  const products = result.details?.products ?? [];
  const hasProducts = products.length > 0;

  return (
    <div className="p-4 space-y-6">
      {/* Summary */}
      <div className="text-sm">{result.summary}</div>

      {/* Business Context */}
      <section className="space-y-3">
        {result.description && (
          <InfoBlock icon={FileText} label="WHAT IT TESTS">
            {result.description}
          </InfoBlock>
        )}
        {result.user_outcome && (
          <InfoBlock icon={Target} label="USER OUTCOME">
            {result.user_outcome}
          </InfoBlock>
        )}
        {result.business_value && (
          <InfoBlock icon={Briefcase} label="BUSINESS VALUE">
            {result.business_value}
          </InfoBlock>
        )}
        {result.apis_tested && result.apis_tested.length > 0 && (
          <InfoBlock icon={Link2} label="COVERAGE">
            <div className="flex flex-wrap gap-1 mt-1">
              {result.apis_tested.map((api, i) => (
                <Badge key={i} variant="outline" className="text-[10px] font-mono">
                  {api}
                </Badge>
              ))}
            </div>
          </InfoBlock>
        )}
      </section>

      {/* Execution Trace */}
      {result.steps.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">
            EXECUTION TRACE
          </h3>
          <div className="space-y-2">
            {result.steps.map((step, i) => (
              <StepCard key={i} step={step} />
            ))}
          </div>
        </section>
      )}

      {/* Products */}
      {hasProducts && (
        <section>
          <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
            <Package className="w-4 h-4" />
            PRODUCTS RETURNED ({products.length})
          </h3>
          <ProductGrid products={products} onSelect={onSelectProduct} />
        </section>
      )}

      {/* Debug Details */}
      {result.details?.conversation_id && (
        <section className="text-xs text-muted-foreground">
          <span className="font-mono">
            conversation_id: {result.details.conversation_id}
          </span>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suite Results (multiple tests)
// ---------------------------------------------------------------------------

function SuiteResults({
  results,
  onSelectProduct,
}: {
  results: ExecutedResult[];
  onSelectProduct: (p: ProductItem) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="divide-y divide-border/50">
      {results.map((result) => {
        const isExpanded = expandedId === result.case_id;
        const products = result.details?.products ?? [];

        return (
          <div key={result.case_id} className="bg-background">
            {/* Collapsed header */}
            <div
              className={cn(
                'flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-accent/30',
                isExpanded && 'bg-muted/30'
              )}
              onClick={() => setExpandedId(isExpanded ? null : result.case_id)}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 shrink-0" />
              )}
              {result.success ? (
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500 shrink-0" />
              )}
              <span className="flex-1 truncate">{result.name}</span>
              <span className="text-xs text-muted-foreground">
                {result.duration_ms ? `${Math.round(result.duration_ms)}ms` : '-'}
              </span>
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-4 pb-4 pl-10 space-y-4">
                <div className="text-sm">{result.summary}</div>

                {result.user_outcome && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">Outcome:</span> {result.user_outcome}
                  </div>
                )}

                {result.steps.length > 0 && (
                  <div className="space-y-2">
                    {result.steps.map((step, i) => (
                      <StepCard key={i} step={step} compact />
                    ))}
                  </div>
                )}

                {products.length > 0 && (
                  <ProductGrid products={products.slice(0, 5)} onSelect={onSelectProduct} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step Card
// ---------------------------------------------------------------------------

function StepCard({ step, compact = false }: { step: TestStep; compact?: boolean }) {
  const [showBody, setShowBody] = useState(false);

  // Extract key info from request_body for inline display
  const queryPreview = step.request_body
    ? extractQueryPreview(step.request_body)
    : null;

  return (
    <div
      className={cn(
        'border rounded-lg',
        step.success ? 'border-border/50' : 'border-red-500/30 bg-red-500/5'
      )}
    >
      {/* Step header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2',
          step.request_body && 'cursor-pointer'
        )}
        onClick={() => step.request_body && setShowBody(!showBody)}
      >
        {step.success ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
        ) : (
          <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
        )}

        <Badge variant="outline" className="text-[10px] font-mono shrink-0">
          {step.method}
        </Badge>

        <code className="text-xs font-mono text-muted-foreground">{step.endpoint}</code>

        <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />

        <span className="text-xs flex-1 truncate">
          {step.status_code && (
            <Badge
              variant={step.status_code < 400 ? 'secondary' : 'destructive'}
              className="text-[10px] mr-1"
            >
              {step.status_code}
            </Badge>
          )}
          {step.response_summary}
        </span>

        {!compact && step.duration_ms && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            {Math.round(step.duration_ms)}ms
          </span>
        )}

        {step.request_body && (
          <ChevronRight
            className={cn(
              'w-3 h-3 text-muted-foreground transition-transform shrink-0',
              showBody && 'rotate-90'
            )}
          />
        )}
      </div>

      {/* Query preview - always visible if present */}
      {queryPreview && !showBody && (
        <div className="px-3 pb-2 text-xs">
          <span className="text-muted-foreground">Input: </span>
          <span className="font-medium text-blue-600 dark:text-blue-400">
            {queryPreview}
          </span>
        </div>
      )}

      {/* Request body (collapsible - full details) */}
      {showBody && step.request_body && (
        <div className="px-3 pb-2 border-t border-border/30">
          <pre className="text-[10px] text-muted-foreground overflow-auto max-h-32 mt-2">
            {JSON.stringify(step.request_body, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Extract a human-readable preview from request body.
 * Shows query text, image indicator, or key parameters.
 */
function extractQueryPreview(body: Record<string, unknown>): string | null {
  // Text query
  if (body.query && typeof body.query === 'string') {
    return `"${body.query}"`;
  }

  // Image search
  if (body.images && Array.isArray(body.images)) {
    const imageCount = body.images.length;
    const queryPart = body.query ? ` + "${body.query}"` : '';
    return `[${imageCount} image${imageCount > 1 ? 's' : ''}]${queryPart}`;
  }

  // Other parameters - show first string value
  for (const [key, value] of Object.entries(body)) {
    if (typeof value === 'string' && value.length < 100) {
      return `${key}: "${value}"`;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Info Block
// ---------------------------------------------------------------------------

function InfoBlock({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      </div>
      <div className="text-sm pl-5">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product Grid
// ---------------------------------------------------------------------------

function ProductGrid({
  products,
  onSelect,
}: {
  products: ProductItem[];
  onSelect: (p: ProductItem) => void;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
      {products.map((product) => (
        <div
          key={product.semantic_id}
          className={cn(
            'group relative bg-card border border-border rounded-lg overflow-hidden',
            'cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all'
          )}
          onClick={() => onSelect(product)}
        >
          {/* Image */}
          <div className="aspect-square bg-muted relative">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-6 h-6 text-muted-foreground/30" />
              </div>
            )}

            {/* Similarity badge */}
            {product.similarity != null && (
              <Badge
                variant="secondary"
                className="absolute top-1 right-1 text-[9px] bg-background/80"
              >
                {Math.round(product.similarity * 100)}%
              </Badge>
            )}
          </div>

          {/* Info */}
          <div className="p-1.5">
            <p className="text-xs font-medium truncate">{product.name}</p>
            {product.price != null && (
              <p className="text-xs text-green-600">${product.price.toFixed(0)}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
